/**
 * „Meine Schichten" — die Mitarbeitersicht.
 *
 * Als Erstes und Einziges auf dem Handy: Wer hier landet, will wissen, wann er
 * arbeitet. Eine Spalte, große Ziele, keine Tabelle. Das Wochenraster für die
 * Planung ist ein Desktop-Werkzeug; das hier ist Daumenbedienung.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo, useState } from 'react'

import {
  api,
  ApiError,
  type Absence,
  type LeaveBalance,
  type ListResponse,
  type OpenShift,
} from '../lib/api'
import { Calendar } from '../components/Calendar'
import { isoDate, monthGridRange } from '../lib/calendar-range'
import { oneOf, usePersistentState } from '../lib/persistent-state'
import { absencesToEvents, readableTextColor } from '../lib/calendar-events'
import { listItemVariants, listVariants, springSoft } from '../lib/motion'
import { Banner, Button, Card, Chip, Dialog, SelectField, Spinner, TextField, useToast } from '../ui'
import './me.css'

type MyShift = {
  id: string
  assignment_id: string
  starts_at: string
  ends_at: string
  break_minutes: number
  location_name: string
  position_name: string
  position_color: string
  colleagues: string[]
  swap_status: string | null
}

type Availability = {
  id: string
  weekday: number
  from_time: string
  to_time: string
  kind: string
}

const WEEKDAYS = ['', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag']

const ME_REITER = ['shifts', 'open', 'availability', 'absences', 'settings'] as const
type MeReiter = (typeof ME_REITER)[number]

const ME_SICHTEN = ['dayGridMonth', 'timeGridWeek', 'listWeek'] as const
type MeSicht = (typeof ME_SICHTEN)[number]

export function MePage() {
  // Reiterwahl übersteht das Neuladen — wer bei „Verfügbarkeit" arbeitet,
  // will nach F5 nicht wieder bei „Schichten" anfangen.
  const [tab, setTab] = usePersistentState<MeReiter>('me.tab', 'shifts', oneOf(ME_REITER))

  return (
    <div className="stack">
      <h1>Meine Schichten</h1>

      <div className="row" role="tablist" aria-label="Bereiche">
        {(
          [
            ['shifts', 'Schichten'],
            ['open', 'Offene Schichten'],
            ['availability', 'Verfügbarkeit'],
            ['absences', 'Abwesenheit'],
            ['settings', 'Kalender & Hinweise'],
          ] as const
        ).map(([value, label]) => (
          <Button
            key={value}
            variant={tab === value ? 'tonal' : 'text'}
            small
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === 'shifts' && <MyShifts />}
      {tab === 'open' && <OpenShifts />}
      {tab === 'availability' && <MyAvailability />}
      {tab === 'absences' && (
        <div className="stack">
          <MyLeave />
          <MyAbsences />
        </div>
      )}
      {tab === 'settings' && <MySettings />}
    </div>
  )
}

// ---------------------------------------------------------------- Schichten

function MyShifts() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [sicht, setSicht] = usePersistentState<MeSicht>(
    'me.sicht',
    'dayGridMonth',
    oneOf(ME_SICHTEN),
  )
  const today = new Date()
  // ⚠️ Der geladene Bereich beginnt am Anfang des **Monatsrasters**, nicht
  // heute und nicht am Monatsersten. Das Raster zeigt volle Wochen und reicht
  // bis zu sechs Tage in den Vormonat; startete die Abfrage später, wären
  // genau diese Tage leer — und „leer" heißt in einem Kalender sonst „nicht
  // eingeteilt", nicht „nicht geladen".
  const rasteranfang = monthGridRange(today).von
  const from = isoDate(rasteranfang)
  const to = isoDate(new Date(today.getTime() + 42 * 86_400_000))

  const shifts = useQuery({
    queryKey: ['my-shifts', from],
    queryFn: () => api.get<ListResponse<MyShift>>(`/api/me/shifts?from=${from}&to=${to}`),
  })

  // Die eigenen Abwesenheiten gehören ins selbe Bild — sonst sieht der
  // Kalender an einem Urlaubstag einfach leer aus, und leer heißt sonst
  // „nicht eingeteilt".
  const absences = useQuery({
    queryKey: ['my-absences'],
    queryFn: () => api.get<ListResponse<Absence>>('/api/me/absences'),
  })

  // Eigene Schichten und Abwesenheiten in einem Bild. Die Farbe kommt von der
  // Position, die Schrift wird dazu passend hell oder dunkel gewählt — auf
  // gelben Positionen wäre Weiß unlesbar.
  const termine = useMemo(
    () => [
      ...(shifts.data?.items ?? []).map((schicht) => ({
        id: schicht.id,
        // `Date` statt ISO-Zeichenkette — sonst zeigt der Kalender UTC.
        // Begründung in `lib/calendar-events.ts`.
        start: new Date(schicht.starts_at),
        end: new Date(schicht.ends_at),
        title: `${schicht.position_name} · ${schicht.location_name}`,
        backgroundColor: schicht.position_color || undefined,
        textColor: schicht.position_color
          ? readableTextColor(schicht.position_color)
          : undefined,
        editable: false,
      })),
      ...absencesToEvents(absences.data?.items ?? []),
    ],
    [shifts.data, absences.data],
  )

  const offer = useMutation({
    mutationFn: (assignmentId: string) =>
      api.post('/api/swaps', { assignment_id: assignmentId }),
    onSuccess: () => {
      toast('Die Schicht steht jetzt in der Tauschbörse.', 'success')
      queryClient.invalidateQueries({ queryKey: ['my-shifts'] })
      queryClient.invalidateQueries({ queryKey: ['swaps'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (shifts.isPending) return <Spinner />
  const alle = shifts.data?.items ?? []
  // Der Kalender zeigt den ganzen geladenen Zeitraum, die Liste darunter nur
  // das, was noch kommt. Eine Liste „Meine Schichten", die mit vergangenen
  // Tagen aufmacht, beantwortet die falsche Frage.
  const heuteIso = isoDate(today)
  const items = alle.filter((s) => isoDate(new Date(s.starts_at)) >= heuteIso)

  // Kalender oben, Liste darunter. Der Kalender beantwortet „wann arbeite
  // ich?", die Liste trägt die Handlung („abgeben") — beides in einer
  // Darstellung unterzubringen hieße, eine davon zu verstümmeln.
  const kalender = (
    <Card>
      <div className="row row--between" style={{ marginBottom: 'var(--sp-3)' }}>
        <strong>Nächste sechs Wochen</strong>
        <div className="row" role="tablist" aria-label="Darstellung">
          {(
            [
              ['dayGridMonth', 'Monat'],
              ['timeGridWeek', 'Woche'],
              ['listWeek', 'Liste'],
            ] as const
          ).map(([wert, label]) => (
            <Button
              key={wert}
              variant={sicht === wert ? 'tonal' : 'text'}
              small
              role="tab"
              aria-selected={sicht === wert}
              onClick={() => setSicht(wert)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <Calendar
        view={sicht}
        date={today}
        events={termine}
        height={sicht === 'dayGridMonth' ? '620px' : '560px'}
      />
    </Card>
  )

  if (items.length === 0) {
    return (
      <div className="stack">
        {kalender}
        <Card>
          <p className="muted" style={{ margin: 0 }}>
            Für die nächsten sechs Wochen ist nichts für dich eingeplant. Sobald der Plan
            veröffentlicht wird, steht er hier.
          </p>
        </Card>
      </div>
    )
  }

  // Nach Tag gruppieren: „Freitag, 14.08." als Überschrift ist das, wonach
  // man auf dem Handy sucht.
  const byDay = new Map<string, MyShift[]>()
  for (const shift of items) {
    const key = new Date(shift.starts_at).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
    })
    byDay.set(key, [...(byDay.get(key) ?? []), shift])
  }

  return (
    <motion.div
      className="stack"
      variants={listVariants(items.length)}
      initial="hidden"
      animate="visible"
    >
      {kalender}
      {[...byDay.entries()].map(([day, dayShifts]) => (
        <motion.div key={day} variants={listItemVariants} transition={springSoft()}>
          <h3 className="me__day">{day}</h3>
          <div className="stack stack--tight">
            {dayShifts.map((shift) => (
              <article
                key={shift.id}
                className="me__shift"
                style={{ borderLeftColor: shift.position_color }}
              >
                <div className="me__time">
                  {new Date(shift.starts_at).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {' – '}
                  {new Date(shift.ends_at).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
                <div className="me__what">
                  <strong>{shift.position_name}</strong>
                  <span className="muted"> · {shift.location_name}</span>
                </div>
                {shift.break_minutes > 0 && (
                  <div className="muted me__small">{shift.break_minutes} Minuten Pause</div>
                )}
                {shift.colleagues.length > 0 && (
                  <div className="muted me__small">Mit: {shift.colleagues.join(', ')}</div>
                )}
                <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
                  {shift.swap_status ? (
                    <Chip tone="warning">
                      {shift.swap_status === 'claimed'
                        ? 'Übernahme angefragt'
                        : 'zum Tausch angeboten'}
                    </Chip>
                  ) : (
                    <Button
                      variant="outlined"
                      small
                      busy={offer.isPending}
                      onClick={() => offer.mutate(shift.assignment_id)}
                    >
                      Abgeben
                    </Button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </motion.div>
      ))}
    </motion.div>
  )
}

// ------------------------------------------------------------ Verfügbarkeit

function MyAvailability() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [draft, setDraft] = useState<Availability[] | null>(null)

  const current = useQuery({
    queryKey: ['my-availabilities'],
    queryFn: () => api.get<ListResponse<Availability>>('/api/me/availabilities'),
  })

  const save = useMutation({
    mutationFn: (windows: Availability[]) =>
      api.put('/api/me/availabilities', {
        windows: windows.map((w) => ({
          weekday: w.weekday,
          from_time: w.from_time,
          to_time: w.to_time,
          kind: 'available',
        })),
      }),
    onSuccess: () => {
      toast('Verfügbarkeit gespeichert.', 'success')
      queryClient.invalidateQueries({ queryKey: ['my-availabilities'] })
      setDraft(null)
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (current.isPending) return <Spinner />
  const windows = draft ?? current.data?.items ?? []

  return (
    <Card title="Wann kannst du?">
      <div className="stack">
        <Banner tone="info">
          Das ist ein Wunsch, keine Zusage — und keine Absage: Solange du nichts einträgst, plant
          die Schichtleitung ohne Einschränkung. Sobald hier etwas steht, bekommt sie eine
          Warnung, wenn eine Schicht nicht hineinpasst.
        </Banner>

        {windows.length === 0 && <p className="muted">Noch nichts hinterlegt.</p>}

        <AnimatePresence initial={false}>
          {windows.map((window, index) => (
            <motion.div
              key={`${window.weekday}-${index}`}
              className="row"
              layout
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={springSoft()}
            >
              <div style={{ flex: '1 1 130px' }}>
                <SelectField
                  label="Tag"
                  value={String(window.weekday)}
                  onChange={(event) => {
                    const next = [...windows]
                    next[index] = { ...window, weekday: Number(event.target.value) }
                    setDraft(next)
                  }}
                >
                  {WEEKDAYS.slice(1).map((label, day) => (
                    <option key={label} value={day + 1}>
                      {label}
                    </option>
                  ))}
                </SelectField>
              </div>
              <div style={{ flex: '0 1 120px' }}>
                <TextField
                  label="Ab"
                  type="time"
                  value={window.from_time.slice(0, 5)}
                  onChange={(event) => {
                    const next = [...windows]
                    next[index] = { ...window, from_time: `${event.target.value}:00` }
                    setDraft(next)
                  }}
                />
              </div>
              <div style={{ flex: '0 1 120px' }}>
                <TextField
                  label="Bis"
                  type="time"
                  value={window.to_time.slice(0, 5)}
                  onChange={(event) => {
                    const next = [...windows]
                    next[index] = { ...window, to_time: `${event.target.value}:00` }
                    setDraft(next)
                  }}
                />
              </div>
              <Button
                variant="text"
                small
                onClick={() => setDraft(windows.filter((_, i) => i !== index))}
              >
                Entfernen
              </Button>
            </motion.div>
          ))}
        </AnimatePresence>

        <div className="row row--between">
          <Button
            variant="outlined"
            small
            onClick={() =>
              setDraft([
                ...windows,
                {
                  id: crypto.randomUUID(),
                  weekday: 1,
                  from_time: '17:00:00',
                  to_time: '23:00:00',
                  kind: 'available',
                },
              ])
            }
          >
            Zeitfenster hinzufügen
          </Button>
          <Button
            busy={save.isPending}
            disabled={draft === null}
            onClick={() => save.mutate(windows)}
          >
            Speichern
          </Button>
        </div>
      </div>
    </Card>
  )
}

// -------------------------------------------------------------- Abwesenheit

function MyAbsences() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [open, setOpen] = useState(false)

  const absences = useQuery({
    queryKey: ['my-absences'],
    queryFn: () => api.get<ListResponse<Absence>>('/api/me/absences'),
  })

  const withdraw = useMutation({
    mutationFn: (id: string) => api.delete(`/api/me/absences/${id}`),
    onSuccess: () => {
      toast('Antrag zurückgezogen.', 'success')
      queryClient.invalidateQueries({ queryKey: ['my-absences'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const labels: Record<string, string> = {
    vacation: 'Urlaub',
    sick: 'Krank',
    other: 'Abwesend',
  }
  const statusTone: Record<string, 'success' | 'warning' | 'danger'> = {
    approved: 'success',
    requested: 'warning',
    rejected: 'danger',
  }
  const statusLabel: Record<string, string> = {
    approved: 'genehmigt',
    requested: 'wartet',
    rejected: 'abgelehnt',
  }

  return (
    <Card
      title="Abwesenheiten"
      action={
        <Button small onClick={() => setOpen(true)}>
          Beantragen
        </Button>
      }
    >
      <div className="stack stack--tight">
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          Ein Grund wird nicht erfasst und nicht gespeichert — nur die Art und der Zeitraum.
        </p>
        {absences.isPending && <Spinner />}
        {absences.data?.items.length === 0 && <p className="muted">Nichts eingetragen.</p>}
        {absences.data?.items.map((absence) => (
          <div key={absence.id} className="row row--between">
            <span>
              {labels[absence.kind]} ·{' '}
              {new Date(absence.starts_on).toLocaleDateString('de-DE')} –{' '}
              {new Date(absence.ends_on).toLocaleDateString('de-DE')}
            </span>
            <span className="row">
              <Chip tone={statusTone[absence.status]}>{statusLabel[absence.status]}</Chip>
              {absence.status !== 'rejected' && (
                <Button variant="text" small onClick={() => withdraw.mutate(absence.id)}>
                  Zurückziehen
                </Button>
              )}
            </span>
          </div>
        ))}
      </div>
      <AbsenceDialog open={open} onClose={() => setOpen(false)} />
    </Card>
  )
}

function AbsenceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [kind, setKind] = useState('vacation')
  const [from, setFrom] = useState(isoDate(new Date()))
  const [to, setTo] = useState(isoDate(new Date()))
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/me/absences', { kind, starts_on: from, ends_on: to }),
    onSuccess: () => {
      toast('Antrag gestellt. Die Schichtleitung entscheidet.', 'success')
      queryClient.invalidateQueries({ queryKey: ['my-absences'] })
      setError(null)
      onClose()
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <Dialog open={open} onClose={onClose} title="Abwesenheit beantragen">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          create.mutate()
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        <SelectField label="Art" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="vacation">Urlaub</option>
          <option value="sick">Krank</option>
          <option value="other">Sonstiges</option>
        </SelectField>
        <TextField label="Von" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField label="Bis" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          Einen Grund brauchst du nicht anzugeben — es gibt dafür kein Feld.
        </p>
        <div className="row row--between">
          <Button variant="text" type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" busy={create.isPending} disabled={to < from}>
            Beantragen
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

// -------------------------------------------------- Kalender und Hinweise

function MySettings() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [freshUrl, setFreshUrl] = useState<string | null>(null)

  const status = useQuery({
    queryKey: ['my-calendar'],
    queryFn: () => api.get<{ url: string | null; has_token: boolean }>('/api/me/calendar'),
  })

  const create = useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/me/calendar'),
    onSuccess: (result) => {
      setFreshUrl(result.url)
      queryClient.invalidateQueries({ queryKey: ['my-calendar'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const revoke = useMutation({
    mutationFn: () => api.delete('/api/me/calendar'),
    onSuccess: () => {
      setFreshUrl(null)
      toast('Zugang widerrufen.', 'success')
      queryClient.invalidateQueries({ queryKey: ['my-calendar'] })
    },
  })

  return (
    <div className="stack">
      <Card title="Kalender abonnieren">
        <div className="stack">
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            Deine Schichten in Apple Kalender, Google Kalender oder Outlook — automatisch
            aktualisiert. Der Link ist dein Schlüssel: Wer ihn hat, sieht deine Schichten. Gib ihn
            nicht weiter, und widerrufe ihn, wenn du dein Handy verlierst.
          </p>

          {freshUrl && (
            <Banner tone="success">
              <div className="stack stack--tight" style={{ width: '100%' }}>
                <strong>Diesen Link jetzt kopieren — er wird nicht noch einmal angezeigt.</strong>
                <code className="me__url">{freshUrl}</code>
                <Button
                  small
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard?.writeText(freshUrl)
                    toast('Link kopiert.', 'success')
                  }}
                >
                  Kopieren
                </Button>
              </div>
            </Banner>
          )}

          <div className="row">
            <Button busy={create.isPending} onClick={() => create.mutate()}>
              {status.data?.has_token ? 'Neuen Link erzeugen' : 'Link erzeugen'}
            </Button>
            {status.data?.has_token && (
              <Button variant="text" busy={revoke.isPending} onClick={() => revoke.mutate()}>
                Widerrufen
              </Button>
            )}
          </div>
          {status.data?.has_token && !freshUrl && (
            <p className="muted" style={{ fontSize: '0.8125rem' }}>
              Ein Zugang ist eingerichtet. Der Link selbst lässt sich nicht erneut anzeigen — wir
              speichern ihn nicht im Klartext. Erzeuge notfalls einen neuen; der alte wird dabei
              ungültig.
            </p>
          )}
        </div>
      </Card>

      <PushCard />
    </div>
  )
}

function PushCard() {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [enabled, setEnabled] = useState<boolean | null>(null)

  const config = useQuery({
    queryKey: ['push-config'],
    queryFn: () => api.get<{ enabled: boolean; public_key: string | null }>('/api/push/config'),
  })

  if (config.isPending) return <Spinner />
  if (!config.data?.enabled) {
    return (
      <Card title="Hinweise aufs Handy">
        <Banner tone="info">
          Push-Benachrichtigungen sind für diesen Server nicht eingerichtet. Du bekommst weiterhin
          E-Mails — das ist ohnehin der verlässliche Weg.
        </Banner>
      </Card>
    )
  }

  const supported =
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window

  const subscribe = async () => {
    setBusy(true)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        toast('Ohne Erlaubnis des Browsers geht es nicht.', 'error')
        return
      }
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: config.data.public_key!,
      })
      const json = subscription.toJSON()
      await api.post('/api/push/subscribe', {
        endpoint: subscription.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
      })
      setEnabled(true)
      toast('Benachrichtigungen sind an.', 'success')
    } catch {
      toast('Das hat nicht geklappt. Erlaubt dein Browser Benachrichtigungen?', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Hinweise aufs Handy">
      <div className="stack">
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          Ein kurzer Hinweis, wenn ein neuer Plan da ist oder sich etwas an deinen Schichten
          ändert. Was genau, steht nicht auf dem Sperrbildschirm — nur, dass es etwas gibt.
        </p>
        {!supported ? (
          <Banner tone="info">
            Dieser Browser kann keine Benachrichtigungen. Auf dem iPhone geht es, wenn du die App
            über „Zum Home-Bildschirm" installierst.
          </Banner>
        ) : (
          <Button busy={busy} disabled={enabled === true} onClick={subscribe}>
            {enabled ? 'Eingeschaltet' : 'Benachrichtigungen einschalten'}
          </Button>
        )}
      </div>
    </Card>
  )
}

// ------------------------------------------------------------------ Urlaub

/**
 * Der eigene Urlaubsstand.
 *
 * Steht über den Abwesenheiten, weil die Frage „wie viele Tage habe ich noch?"
 * derjenigen vorausgeht, ob man einen Antrag stellt.
 */
function MyLeave() {
  const jahr = new Date().getFullYear()
  const stand = useQuery({
    queryKey: ['my-leave', jahr],
    queryFn: () => api.get<LeaveBalance>(`/api/me/leave?year=${jahr}`),
  })

  if (stand.isPending) return <Spinner />
  const daten = stand.data
  if (!daten) return null

  // Ohne hinterlegten Anspruch wird nichts behauptet — schon gar keine Null.
  if (daten.entitled_days === null) {
    return (
      <Card title={`Urlaub ${jahr}`}>
        <p className="muted" style={{ margin: 0 }}>
          Für dieses Jahr ist noch kein Urlaubsanspruch hinterlegt. Deine Betriebsleitung kann
          ihn eintragen.
        </p>
        {daten.taken_days > 0 && (
          <p className="muted" style={{ marginBottom: 0 }}>
            Genommen hast du bisher {daten.taken_days} Tage.
          </p>
        )}
      </Card>
    )
  }

  return (
    <Card title={`Urlaub ${jahr}`}>
      <div className="grid-2">
        {[
          ['Anspruch', daten.entitled_days],
          ['Übertrag', daten.carryover_days],
          ['Genommen', String(daten.taken_days)],
          ['Rest', daten.remaining_days],
        ].map(([label, wert]) => (
          <div className="stat" key={label}>
            <span className="stat__value">{wert}</span>
            <span className="stat__label">{label}</span>
          </div>
        ))}
      </div>
      {daten.pending_days > 0 && (
        <p className="muted" style={{ marginBottom: 0 }}>
          Zusätzlich sind {daten.pending_days} Tage beantragt, aber noch nicht entschieden —
          danach blieben {daten.remaining_after_pending} Tage.
        </p>
      )}
    </Card>
  )
}

// --------------------------------------------------------- Offene Schichten

/** Unbesetzte Schichten, auf die man sich melden kann. */
function OpenShifts() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const heute = new Date()
  const from = isoDate(heute)
  const bis = new Date(heute)
  bis.setDate(bis.getDate() + 28)
  const to = isoDate(bis)

  const schichten = useQuery({
    queryKey: ['open-shifts', from, to],
    queryFn: () => api.get<ListResponse<OpenShift>>(`/api/me/open-shifts?from=${from}&to=${to}`),
  })

  const bewerben = useMutation({
    mutationFn: (shiftId: string) => api.post(`/api/shifts/${shiftId}/apply`),
    onSuccess: () => {
      toast('Bewerbung abgeschickt. Die Planung entscheidet.', 'success')
      queryClient.invalidateQueries({ queryKey: ['open-shifts'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (schichten.isPending) return <Spinner />
  const items = schichten.data?.items ?? []

  return (
    <Card title="Offene Schichten">
      {items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Zurzeit ist nichts ausgeschrieben. Hier erscheinen veröffentlichte Schichten, für die
          noch jemand gesucht wird — Tage, an denen du abwesend bist, sind ausgenommen.
        </p>
      ) : (
        <ul className="stack stack--tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((schicht) => (
            <li key={schicht.shift_id} className="row row--between">
              <span>
                <strong>
                  {new Date(schicht.starts_at).toLocaleString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {' – '}
                  {new Date(schicht.ends_at).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </strong>
                <span className="muted">
                  {' '}
                  · {schicht.position_name} · {schicht.location_name} · noch{' '}
                  {schicht.needed - schicht.assigned} gesucht
                </span>
              </span>
              {schicht.applied ? (
                <Chip tone="warning">beworben</Chip>
              ) : (
                <Button
                  small
                  busy={bewerben.isPending}
                  onClick={() => bewerben.mutate(schicht.shift_id)}
                >
                  Ich übernehme
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
